/**
 * Portuguese Legal Translations
 * 法律文档翻译模块 - 葡萄牙语
 * 
 * 包含隐私政策 (privacy) 和服务条款 (terms) 等法律文档的翻译
 */

import type { LegalTranslations } from '../../types';

export const ptLegal: LegalTranslations = {
  privacy: {
    title: 'Política de Privacidade',
    subtitle: 'Como coletamos, usamos e protegemos suas informações pessoais.',
    lastUpdated: 'Última Atualização',
    effectiveDate: 'Data de Vigência',
    meta: {
      lastUpdatedValue: '6 de fevereiro de 2026',
      effectiveDateValue: '6 de fevereiro de 2026',
    },
    introduction: 'Fujian Jinghui Environmental Technology Co., Ltd. ("nós", "nosso" ou "nossa") está comprometida em proteger sua privacidade. Esta Política de Privacidade explica como coletamos, usamos, divulgamos e protegemos suas informações quando você visita nosso site e usa nossos serviços.',
    sections: {
      infoCollect: {
        title: '1. Informações Que Coletamos',
        description: 'Podemos coletar informações pessoais que você nos fornece voluntariamente quando:',
        items: [
          'Se registra em nosso site ou faz uma consulta',
          'Solicita uma cotação ou informações do produto',
          'Se inscreve em nossa newsletter',
          'Entra em contato com nossa equipe de vendas ou suporte',
          'Participa de pesquisas ou promoções',
        ],
      },
      typesCollected: {
        title: '2. Tipos de Informações Coletadas',
        items: [
          'Informações de Identificação Pessoal: Nome, endereço de e-mail, número de telefone, nome da empresa, cargo',
          'Informações Demográficas: País, região, preferências de idioma',
          'Informações Técnicas: Endereço IP, tipo de navegador, sistema operacional, URLs de referência',
          'Dados de Comunicação: Mensagens, consultas, comentários e solicitações de suporte',
        ],
      },
      howUse: {
        title: '3. Como Usamos Suas Informações',
        description: 'Usamos as informações que coletamos para:',
        items: [
          'Fornecer, manter e melhorar nossos produtos e serviços',
          'Responder às suas consultas e fornecer suporte ao cliente',
          'Enviar informações do produto, cotações e materiais promocionais',
          'Processar transações e enviar informações relacionadas',
          'Monitorar e analisar tendências, uso e atividades',
          'Detectar, investigar e prevenir transações fraudulentas',
          'Cumprir obrigações legais',
        ],
      },
      dataSecurity: {
        title: '4. Segurança de Dados',
        description: 'Implementamos medidas de segurança técnicas e organizacionais apropriadas para proteger suas informações pessoais contra acesso não autorizado, alteração, divulgação ou destruição. No entanto, nenhum método de transmissão pela Internet ou armazenamento eletrônico é 100% seguro.',
        encryption: 'Usamos criptografia padrão da indústria (SSL/TLS) para proteger os dados transmitidos entre seu navegador e nossos servidores.',
      },
      cookies: {
        title: '5. Cookies e Tecnologias de Rastreamento',
        description: 'Nosso site pode usar cookies, web beacons e tecnologias de rastreamento semelhantes para coletar informações sobre suas atividades de navegação. Você pode configurar seu navegador para recusar todos os cookies ou para indicar quando um cookie está sendo enviado.',
      },
      thirdParty: {
        title: '6. Divulgação a Terceiros',
        description: 'Não vendemos, comercializamos ou transferimos suas informações de identificação pessoal a terceiros, exceto nas seguintes circunstâncias:',
        items: [
          'Com provedores de serviços que auxiliam em nossas operações comerciais',
          'Quando exigido por lei, intimação ou outro processo legal',
          'Para proteger nossos direitos, privacidade, segurança ou propriedade',
          'Em conexão com uma fusão, aquisição ou venda de ativos',
        ],
      },
      dataRetention: {
        title: '7. Retenção de Dados',
        description: 'Reteremos suas informações pessoais pelo tempo necessário para cumprir os propósitos descritos nesta Política de Privacidade, a menos que a lei exija ou permita um período de retenção mais longo.',
      },
      yourRights: {
        title: '8. Seus Direitos',
        description: 'Você tem o direito de:',
        items: [
          'Acessar e receber uma cópia de seus dados pessoais',
          'Rectificar dados pessoais imprecisos',
          'Solicitar a exclusão de seus dados pessoais',
          'Objetar ao processamento de seus dados pessoais',
          'Solicitar a restrição do processamento',
          'Portabilidade de dados',
        ],
      },
      children: {
        title: '9. Privacidade de Crianças',
        description: 'Nosso site não é destinado a crianças menores de 16 anos. Não coletamos intencionalmente informações pessoais de crianças menores de 16 anos.',
      },
      international: {
        title: '10. Transferências Internacionais de Dados',
        description: 'Suas informações podem ser transferidas e processadas em países diferentes do seu país de residência. Esses países podem ter leis de proteção de dados diferentes das leis do seu país.',
      },
      changes: {
        title: '11. Alterações Nesta Política',
        description: 'Podemos atualizar esta Política de Privacidade de tempos em tempos. Iremos notificá-lo de quaisquer alterações postando a nova Política de Privacidade nesta página e atualizando a data "Última Atualização" no topo desta política.',
      },
      contactUs: {
        title: '12. Contate-nos',
        description: 'Se você tiver alguma dúvida sobre esta Política de Privacidade ou nossas práticas de dados, entre em contato conosco:',
        email: 'E-mail',
        address: 'Endereço',
        company: 'Fujian Jinghui Environmental Technology Co., Ltd.',
        addressLine: 'Edifício 25, Parque de Ciência e Tecnologia Wanyang, 26 Rua Tingwang, Vila Tingjiang, Distrito Mawei, Fuzhou, Fujian, China',
      },
    },
  },
  terms: {
    title: 'Termos de Serviço',
    subtitle: 'Por favor, leia estes termos cuidadosamente antes de usar nosso site e serviços.',
    lastUpdated: 'Última Atualização',
    effectiveDate: 'Data de Vigência',
    meta: {
      lastUpdatedValue: '6 de fevereiro de 2026',
      effectiveDateValue: '6 de fevereiro de 2026',
    },
    introduction: 'Bem-vindo ao site da Fujian Jinghui Environmental Technology Co., Ltd. Ao acessar ou usar este site, você concorda em estar vinculado a estes Termos de Serviço e todas as leis e regulamentos aplicáveis. Se você não concordar com qualquer parte destes termos, não poderá usar nosso site.',
    importantNotice: {
      title: 'Aviso Importante',
      description: 'Ao usar este site, você reconhece que leu, entendeu e concorda em estar vinculado a estes Termos de Serviço. Se você estiver usando este site em nome de uma empresa ou organização, você representa que tem autoridade para vincular tal entidade a estes termos.',
    },
    sections: {
      useWebsite: {
        title: '1. Uso do Site',
        description: 'Este site é fornecido pela Fujian Jinghui Environmental Technology Co., Ltd. apenas para fins informativos. Ao usar este site, você concorda em:',
        items: [
          'Usar o site apenas para fins legais e de acordo com estes Termos',
          'Não usar o site de qualquer maneira que possa danificar, desativar, sobrecarregar ou prejudicá-lo',
          'Não tentar obter acesso não autorizado a qualquer parte do site',
          'Não usar o site para transmitir vírus, worms ou código prejudicial',
          'Respeitar os direitos de propriedade intelectual da JH Heatsup e outros',
        ],
      },
      intellectualProperty: {
        title: '2. Direitos de Propriedade Intelectual',
        description: 'Todo o conteúdo deste site, incluindo mas não limitado a texto, gráficos, logotipos, imagens, clipes de áudio, downloads digitais e software, é propriedade da Fujian Jinghui Environmental Technology Co., Ltd. ou seus fornecedores de conteúdo e é protegido por leis internacionais de direitos autorais, marcas comerciais e outras leis de propriedade intelectual.',
        restriction: 'Você não pode reproduzir, distribuir, modificar, criar obras derivadas, exibir publicamente, executar publicamente ou explorar qualquer conteúdo deste site sem nosso consentimento prévio por escrito.',
      },
      productInfo: {
        title: '3. Informações e Preços do Produto',
        description: 'Todos os produtos e serviços apresentados neste site estão sujeitos a disponibilidade. Reservamo-nos o direito de descontinuar qualquer produto ou serviço a qualquer momento.',
        pricing: 'Os preços e especificações estão sujeitos a alterações sem aviso prévio. Fazemos o possível para garantir a precisão das informações do produto, mas podem ocorrer erros. Reservamo-nos o direito de corrigir quaisquer erros, imprecisões ou omissões e de alterar ou atualizar informações a qualquer momento.',
        currency: 'Todos os preços são cotados em USD, salvo indicação em contrário. Os custos de envio, impostos e taxas são adicionais e serão calculados durante o processo de pedido.',
      },
      quotesOrders: {
        title: '4. Cotações e Pedidos',
        description: 'As cotações fornecidas através deste site são válidas por 30 dias, salvo indicação em contrário. Todos os pedidos estão sujeitos a aceitação pela JH Heatsup.',
        contract: 'Ao enviar um pedido, você concorda em comprar os produtos de acordo com nossos termos vigentes naquela época, incluindo preços, envio e termos de pagamento. Um contrato vinculante é formado apenas quando confirmamos a aceitação do seu pedido.',
      },
      warranties: {
        title: '5. Garantias e Isenções de Responsabilidade',
        description: 'ESTE SITE E TODO O CONTEÚDO, PRODUTOS E SERVIÇOS FORNECIDOS ATRAVÉS DELE SÃO FORNECIDOS "COMO ESTÃO" E "COMO DISPONÍVEIS" SEM GARANTIAS DE QUALQUER TIPO, EXPRESSAS OU IMPLÍCITAS.',
        disclaimer: 'JH Heatsup REJEITA TODAS AS GARANTIAS, EXPRESSAS OU IMPLÍCITAS, INCLUINDO, MAS NÃO LIMITADO A, GARANTIAS IMPLÍCITAS DE COMERCIABILIDADE, ADEQUAÇÃO A UM PROPÓSITO PARTICULAR, TÍTULO E NÃO VIOLAÇÃO. Não garantimos que o site será ininterrupto, pontual, seguro ou livre de erros.',
      },
      liability: {
        title: '6. Limitação de Responsabilidade',
        description: 'EM NENHUM CASO A FUJIAN JINGHUI ENVIRONMENT TECHNOLOGY CO., LTD., SEUS DIRIGENTES, DIRETORES, FUNCIONÁRIOS OU AGENTES SERÃO RESPONSÁVEIS POR QUAISQUER DANOS INDIRETOS, INCIDENTAIS, ESPECIAIS, CONSEQUENCIAIS OU PUNITIVOS DECORRENTES DE OU RELACIONADOS AO SEU USO DESTE SITE OU QUAISQUER PRODUTOS OU SERVIÇOS COMPRADOS DE NÓS.',
        limit: 'Nossa responsabilidade total por qualquer reclamação decorrente de ou relacionada a estes termos ou nossos produtos e serviços não excederá o valor pago por você pelos produtos ou serviços que deram origem a tal reclamação.',
      },
      privacy: {
        title: '7. Política de Privacidade',
        description: 'Seu uso deste site também é regido por nossa Política de Privacidade, que é incorporada a estes Termos por referência. Por favor, revise nossa Política de Privacidade para entender como coletamos, usamos e protegemos suas informações.',
      },
      thirdPartyLinks: {
        title: '8. Links de Terceiros',
        description: 'Este site pode conter links para sites de terceiros que não são de propriedade ou controlados pela JH Heatsup. Não temos controle sobre, e não assumimos responsabilidade pelo conteúdo, políticas de privacidade ou práticas de quaisquer sites de terceiros. Você acessa esses links por sua própria conta e risco.',
        recommendation: 'Incentivamos você a revisar os termos e a política de privacidade de qualquer site de terceiros que visitar.',
      },
      governingLaw: {
        title: '9. Lei Aplicável e Disputas',
        description: 'Estes Termos serão regidos e interpretados de acordo com as leis da República Popular da China, sem consideração às suas disposições de conflito de leis.',
        disputes: 'Qualquer disputa decorrente de ou relacionada a estes Termos ou seu uso deste site será resolvida através de negociações de boa-fé entre as partes. Se tais negociações falharem, a disputa será submetida a arbitragem vinculante de acordo com as regras da Comissão Chinesa de Arbitragem de Comércio Internacional.',
      },
      export: {
        title: '10. Controles de Exportação',
        description: 'Você concorda em cumprir todas as leis e regulamentos de controle de exportação aplicáveis. Você representa que não está localizado sob o controle de, ou é nacional ou residente de, qualquer país sujeito a sanções ou embargos abrangentes dos EUA.',
      },
      termination: {
        title: '11. Rescisão',
        description: 'Podemos rescindir ou suspender seu acesso a este site imediatamente, sem aviso prévio ou responsabilidade, por qualquer motivo, incluindo violação destes Termos. Após a rescisão, seu direito de usar o site cessará imediatamente.',
      },
      changes: {
        title: '12. Alterações Nestes Termos',
        description: 'Reservamo-nos o direito de modificar ou substituir estes Termos a qualquer momento. Se uma revisão for significativa, forneceremos aviso 30 dias antes de quaisquer novos termos entrarem em vigor. Seu uso contínuo do site após tais alterações constitui aceitação dos novos Termos.',
      },
      severability: {
        title: '13. Divisibilidade',
        description: 'Se qualquer disposição destes Termos for considerada inválida, ilegal ou inexequível, tal disposição será modificada ao mínimo necessário para torná-la executável, e as disposições restantes continuarão em pleno vigor e efeito.',
      },
      contactUs: {
        title: '14. Informações de Contato',
        description: 'Se você tiver alguma dúvida sobre estes Termos de Serviço, entre em contato conosco:',
        email: 'E-mail',
        address: 'Endereço',
        company: 'Fujian Jinghui Environmental Technology Co., Ltd.',
        addressLine: 'Edifício 25, Parque de Ciência e Tecnologia Wanyang, 26 Rua Tingwang, Vila Tingjiang, Distrito Mawei, Fuzhou, Fujian, China',
      },
    },
  },
  installationGuide: {
    header: {
      title: 'Guia de Instalação',
      subtitle: 'Siga estas etapas para uma instalação segura e eficiente do seu aquecedor JH Heatsup.',
    },
    safety: {
      title: 'Segurança em Primeiro Lugar',
      description:
        'A instalação deve ser realizada por um eletricista qualificado de acordo com os códigos elétricos locais. Certifique-se sempre de que o fornecimento de energia esteja desligado no painel principal antes de iniciar a instalação.',
    },
    toolsTitle: 'Ferramentas Necessárias',
    tools: {
      electricDrill: 'Furadeira Elétrica',
      screwdriver: 'Chave de Fenda',
      level: 'Nível',
      measuringTape: 'Trena',
      wireStrippers: 'Descascador de Fios',
      ladder: 'Escada',
    },
    stepsTitle: 'Etapas de Instalação',
    steps: {
      step1: {
        title: 'Posicionamento e Medição',
        description:
          'Escolha um local no teto ou na parede alta. Para um desempenho ideal, o aquecedor deve ser instalado a uma altura de 2,2 m a 2,5 m do chão. Marque os pontos de perfuração de acordo com as dimensões do suporte de montagem fornecidas na caixa.',
        bullet1: 'Mantenha uma distância mínima de 0,5 m das paredes laterais.',
        bullet2: 'Garanta uma distância mínima de 0,2 m do teto (se montado na parede).',
      },
      step2: {
        title: 'Montagem dos Suportes',
        description:
          'Perfure furos nas posições marcadas e insira os parafusos de expansão. Fixe os suportes de montagem de forma segura à superfície. Deslize os suportes deslizantes traseiros do aquecedor nos suportes montados e aperte os parafusos de ajuste para definir o ângulo desejado (0°, 15°, 30° ou 45°).',
      },
      step3: {
        title: 'Conexão Elétrica',
        description:
          'Conecte o cabo de alimentação à rede elétrica principal. Assegure uma aterramento adequado. Para unidades com controle remoto, certifique-se de que o olho receptor esteja visível para que o controle remoto funcione efetivamente.',
        wireLive: 'L — Fase',
        wireLiveColor: 'Marrom',
        wireNeutral: 'N — Neutro',
        wireNeutralColor: 'Azul',
        wireEarth: 'E — Terra',
        wireEarthColor: 'Amarelo / Verde',
      },
      step4: {
        title: 'Teste',
        description:
          'Ligue a energia. O aquecedor deve atingir a temperatura de operação completa em 5 minutos. Você pode notar um leve cheiro durante o primeiro uso; isso é normal e se dissipará rapidamente.',
      },
    },
    download: {
      title: 'Ver Manual do Produto',
      subtitle: 'Acesse o manual completo de instalação com diagramas para o seu modelo específico.',
      jhnr: 'Ver Manual',
      industrial: 'Ver Manual',
    },
  },
  warrantyPolicy: {
    header: {
      title: 'Política de Garantia',
      subtitle: 'Nosso compromisso com a qualidade e sua tranquilidade.',
    },
    overview: {
      standardCoverage: 'Cobertura Padrão',
      standardCoverageDesc:
        'Os produtos JH Heatsup são garantidos contra defeitos de materiais e mão de obra sob uso normal.',
      warrantyPeriod: 'Período de Garantia',
      residentialPeriod: '2 Anos para aquecedores residenciais.',
      industrialPeriod: '1 Ano para aquecedores industriais e controladores.',
    },
    sections: {
      scope: {
        title: '1. Escopo da Garantia',
        description:
          'Esta garantia se aplica ao comprador original do produto e é intransferível. Cobre a reparação ou substituição de qualquer parte do produto que comprove ser defeituosa em material ou mão de obra dentro do período de garantia.',
      },
      notCovered: {
        title: '2. O Que Não Está Coberto?',
        description: 'Esta garantia não cobre danos resultantes de:',
        item1: 'Instalação incorreta que não esteja em conformidade com o manual fornecido.',
        item2: 'Uso indevido, abuso, negligência ou acidente.',
        item3: 'Alteração ou modificação do produto.',
        item4: 'Surtos de energia ou raios.',
        item5: 'Desgaste normal (por exemplo, descoloração da superfície ao longo do tempo).',
      },
      claim: {
        title: '3. Como Fazer uma Reclamação',
        description: 'Para obter serviço de garantia, entre em contato com nossa equipe de suporte com as seguintes informações:',
        proofOfPurchase: 'Comprovante de compra (Fatura/Recibo)',
        defectDescription: 'Descrição e fotos/vídeo do defeito',
        serialNumber: 'Número de série da unidade',
        contactEmail: 'E-mail de contato',
      },
    },
  },
};
